import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { statusStyles } from './Orders';
import { apiFetch, toImageUrl, formatDate } from '../../utils/api';
import OrderTracker from './OrderTracker';

const rupees = (n) => `₹${Number(n).toLocaleString('en-IN')}`;

function Card({ title, children, className = '' }) {
    return (
        <section className={`rounded-xl border border-gray-200 bg-white p-5 shadow-sm ${className}`}>
            {title && <h2 className="mb-3 text-sm font-semibold text-gray-900">{title}</h2>}
            {children}
        </section>
    );
}

function SumRow({ label, value, className = '' }) {
    return (
        <div className={`flex justify-between text-sm ${className}`}>
            <span className="text-gray-600">{label}</span>
            <span>{value}</span>
        </div>
    );
}

// small loading placeholder instead of a blank page
function Skeleton() {
    return (
        <div className="animate-pulse space-y-4">
            <div className="h-5 w-32 rounded bg-gray-200" />
            <div className="h-24 rounded-xl bg-gray-200" />
            <div className="grid gap-4 lg:grid-cols-[1fr_340px]">
                <div className="h-64 rounded-xl bg-gray-200" />
                <div className="h-64 rounded-xl bg-gray-200" />
            </div>
        </div>
    );
}

const OrderDetails = () => {
    const { id } = useParams(); // from /myaccount/orders/:id
    const [order, setOrder] = useState(null);
    const [loading, setLoading] = useState(true);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [cancelling, setCancelling] = useState(false);
    const [notice, setNotice] = useState(null); // { type: 'success' | 'error', text }

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        apiFetch(`/orders/${id}`)
            .then(({ ok, data }) => {
                if (!cancelled) setOrder(ok ? data : null);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [id]);

    // customer can cancel until the order is Delivered
    const cancelOrder = async () => {
        setCancelling(true);
        setNotice(null);
        const { ok, data } = await apiFetch(`/orders/${id}/cancel`, { method: 'PUT' });
        if (ok) {
            // update the page right away: status badge, cancel button and tracker
            setOrder((o) => ({
                ...o,
                status: 'Cancelled',
                paymentStatus: o.paymentStatus === 'Paid' ? 'Refunded' : o.paymentStatus,
            }));
            setNotice({ type: 'success', text: data?.message || 'Order cancelled.' });
        } else {
            setNotice({ type: 'error', text: data?.message || 'Could not cancel the order.' });
        }
        setConfirmOpen(false);
        setCancelling(false);
    };

    if (loading) return <Skeleton />;

    if (!order) {
        return (
            <>
                <p className="text-sm text-gray-600">Order not found.</p>
                <Link to="/myaccount/orders" className="text-sm font-semibold text-[#2f6b1f] hover:underline">
                    Back to orders
                </Link>
            </>
        );
    }

    const itemCount = order.items.reduce((sum, i) => sum + i.quantity, 0);
    const canCancel = ['Processing', 'Shipped'].includes(order.status);
    const isCOD = order.paymentMethod === 'COD';
    const a = order.address;

    const paymentPill = isCOD
        ? { text: order.status === 'Cancelled' ? 'No payment due' : 'Pay on delivery', cls: 'bg-gray-100 text-gray-700' }
        : order.paymentStatus === 'Paid'
          ? { text: 'Paid', cls: 'bg-green-100 text-green-800' }
          : order.paymentStatus === 'Refunded'
            ? { text: 'Refunded', cls: 'bg-blue-100 text-blue-800' }
            : { text: 'Payment pending', cls: 'bg-amber-100 text-amber-800' };

    return (
        <>
            <Link to="/myaccount/orders" className="text-sm text-[#2f6b1f] hover:underline">
                ‹ Back to orders
            </Link>

            {/* ---------- header ---------- */}
            <div className="mt-3 flex flex-wrap items-start justify-between gap-3 rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
                <div>
                    <h1 className="text-xl font-semibold">Order #{order.orderNo}</h1>
                    <p className="mt-1 text-sm text-gray-500">
                        Placed on {formatDate(order.createdAt)} · {itemCount} {itemCount === 1 ? 'item' : 'items'}
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <span className={`rounded-full px-3 py-1 text-xs font-semibold ${paymentPill.cls}`}>
                        {paymentPill.text}
                    </span>
                    <span className={`rounded-full px-3 py-1 text-xs font-semibold ${statusStyles[order.status]}`}>
                        {order.status}
                    </span>
                </div>
            </div>

            {/* ---------- messages ---------- */}
            {notice && (
                <div
                    role="status"
                    className={`mt-4 rounded-lg border px-4 py-3 text-sm ${
                        notice.type === 'success'
                            ? 'border-green-200 bg-green-50 text-green-800'
                            : 'border-red-200 bg-red-50 text-red-700'
                    }`}
                >
                    {notice.text}
                </div>
            )}

            {order.status === 'Cancelled' && (
                <div className="mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                    This order was cancelled.
                    {order.paymentStatus === 'Refunded' && ' Your refund has been started and usually reaches you in 5-7 working days.'}
                </div>
            )}

            <div className="mt-4 grid items-start gap-4 lg:grid-cols-[1fr_340px]">
                {/* ================= LEFT ================= */}
                <div className="flex min-w-0 flex-col gap-4">
                    <Card title={`Items (${itemCount})`}>
                        <ul className="divide-y divide-gray-100">
                            {order.items.map((i) => (
                                <li key={i.id} className="flex items-center gap-4 py-3 first:pt-0 last:pb-0">
                                    <div className="h-16 w-16 shrink-0 overflow-hidden rounded-lg bg-[#f6f6f1]">
                                        {i.image && (
                                            <img
                                                src={toImageUrl(i.image)}
                                                alt=""
                                                onError={(e) => (e.currentTarget.style.visibility = 'hidden')}
                                                className="h-full w-full object-cover"
                                            />
                                        )}
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <p className="line-clamp-2 text-sm font-medium">{i.name}</p>
                                        <p className="mt-0.5 text-xs text-gray-500">
                                            {rupees(i.price)} × {i.quantity}
                                        </p>
                                    </div>
                                    <p className="shrink-0 text-sm font-semibold">{rupees(i.price * i.quantity)}</p>
                                </li>
                            ))}
                        </ul>
                    </Card>

                    {/* key makes the tracker reload after the status changes */}
                    <OrderTracker key={order.status} orderId={order.id} />
                </div>

                {/* ================= RIGHT ================= */}
                <div className="flex flex-col gap-4">
                    <Card title="Order summary">
                        <div className="flex flex-col gap-2">
                            <SumRow label="Subtotal" value={rupees(order.subtotal)} />
                            {order.discount > 0 && (
                                <SumRow
                                    label={`Discount${order.coupons.length ? ` (${order.coupons.join(', ')})` : ''}`}
                                    value={`- ${rupees(order.discount)}`}
                                    className="text-[#2f6b1f]"
                                />
                            )}
                            <SumRow label="Shipping" value={order.shipping === 0 ? 'Free' : rupees(order.shipping)} />
                            <div className="mt-1 flex justify-between border-t border-gray-100 pt-3 text-base font-semibold">
                                <span>Total</span>
                                <span>{rupees(order.total)}</span>
                            </div>
                        </div>
                    </Card>

                    <Card title="Delivery address">
                        <p className="text-sm font-medium">{a.name}</p>
                        <p className="mt-1 text-sm leading-relaxed text-gray-600">
                            {a.line}
                            <br />
                            {a.city}, {a.state} - {a.pincode}
                        </p>
                        {a.phone && <p className="mt-2 text-sm text-gray-600">{a.phone}</p>}
                        {order.email && <p className="text-sm text-gray-600">{order.email}</p>}
                        {order.notes && (
                            <p className="mt-3 rounded-md bg-gray-50 px-3 py-2 text-xs text-gray-600">Note: {order.notes}</p>
                        )}
                    </Card>

                    <Card title="Payment and delivery">
                        <div className="flex flex-col gap-2">
                            <SumRow label="Payment method" value={isCOD ? 'Cash on delivery' : 'Online (UPI / Card / Netbanking)'} />
                            <SumRow label="Delivery" value={order.deliveryMethod === 'express' ? 'Express' : 'Standard'} />
                        </div>
                    </Card>

                    {canCancel && (
                        <Card title="Need to cancel?">
                            <p className="text-sm text-gray-600">
                                {order.status === 'Shipped'
                                    ? 'Your order has already shipped, but you can still cancel it until it is delivered.'
                                    : 'You can cancel this order any time before it ships.'}
                                {!isCOD && ' Your payment will be refunded.'}
                            </p>
                            <button
                                onClick={() => setConfirmOpen(true)}
                                className="mt-3 w-full rounded-md border border-red-300 px-4 py-2 text-sm font-medium text-red-600 transition hover:bg-red-50"
                            >
                                Cancel order
                            </button>
                        </Card>
                    )}
                </div>
            </div>

            {/* ---------- confirm dialog ---------- */}
            {confirmOpen && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="cancel-title"
                >
                    <div className="w-full max-w-sm rounded-xl bg-white p-6 shadow-lg">
                        <h2 id="cancel-title" className="text-base font-semibold">
                            Cancel order #{order.orderNo}?
                        </h2>
                        <p className="mt-2 text-sm text-gray-600">
                            {order.status === 'Shipped'
                                ? 'This order has already shipped. Cancelling it cannot be undone.'
                                : 'Cancelling cannot be undone.'}
                            {!isCOD && ' Your payment will be refunded to the original payment method.'}
                        </p>
                        <div className="mt-5 flex gap-3">
                            <button
                                onClick={() => setConfirmOpen(false)}
                                disabled={cancelling}
                                className="flex-1 rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
                            >
                                Keep order
                            </button>
                            <button
                                onClick={cancelOrder}
                                disabled={cancelling}
                                className="flex-1 rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-60"
                            >
                                {cancelling ? 'Cancelling...' : 'Yes, cancel'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default OrderDetails;
